import { useEffect, useState } from 'react';

import { bots } from '@webclient/lib/agent';
import CONFIG from '@webclient/lib/const/bots';

import useAppSelector from '@webclient/hooks/useAppSelector';
import useQueryAfterWorkspaceLoaded from '@webclient/hooks/useQueryAfterWorkspaceLoaded';
import useWorkspace from '@webclient/hooks/useWorkspace';

import { selectBot } from '@webclient/state/slices/app-settings/userSettingSelector';

import {
  AllBotsCategoryType,
  AllBotsCreatorType,
  BotsResponseType,
} from '@webclient/shared-types/bots';

import { shuffle } from 'lodash';

type Props = Partial<{
  limit: number;
  order: 'ASC' | 'DESC';
  category: Exclude<AllBotsCategoryType, 'All' | 'Knowz AI'>;
  creator: Exclude<AllBotsCreatorType, 'Everyone'>;
  shouldShuffle: boolean;
  shouldPin: boolean;
  shouldIncludeKnowzAI: boolean;
  filteredValue?: string;
  filteredCategory?: Exclude<AllBotsCategoryType, 'All' | 'Knowz AI'>;
}>;

export default function useGetBots({
  limit = 100, //? no pagination
  order = 'DESC',
  category = undefined,
  creator = undefined,
  shouldShuffle = false,
  shouldPin = false,
  shouldIncludeKnowzAI = true,
  filteredValue,
  filteredCategory,
}: Props) {
  const isUserAuthenticated = useAppSelector(
    (state) => state.auth.isUserAuthenticated,
  );
  const { isWorkspace, workspace } = useWorkspace();

  const bot = useAppSelector(selectBot);
  const [isStorageReady, setIsStorageReady] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined' && window.localStorage) {
      setIsStorageReady(true);
    }
  }, []);

  return useQueryAfterWorkspaceLoaded({
    queryFn: async () => {
      const { data, headers } = await bots.getAll({
        ...calculateCreator(isWorkspace, creator),
        category,
        limit,
        order,
      });
      let finalBots: unknown;

      if (typeof data === 'string') {
        return shouldIncludeKnowzAI
          ? {
              data: [CONFIG.DEFAULT_KNOWZ_AI_BOT],
              totalCount: 1,
            }
          : {
              data: [],
              totalCount: 0,
            };
      }

      if (!shouldPin) {
        const availableBots = shouldShuffle ? shuffle(data) : data;
        finalBots = shouldIncludeKnowzAI
          ? [CONFIG.DEFAULT_KNOWZ_AI_BOT, ...availableBots]
          : availableBots;
      } else {
        const selectedBot = data.find((botItem) => botItem.id === bot.id);
        const botsWithoutSelected = data.filter(
          (botItem) => botItem.id !== bot.id,
        );

        const availableBots = shouldShuffle
          ? shuffle(botsWithoutSelected)
          : botsWithoutSelected;

        if (selectedBot) {
          finalBots = shouldIncludeKnowzAI
            ? [CONFIG.DEFAULT_KNOWZ_AI_BOT, selectedBot, ...availableBots]
            : [selectedBot, ...availableBots];
        } else {
          finalBots = shouldIncludeKnowzAI
            ? [CONFIG.DEFAULT_KNOWZ_AI_BOT, ...availableBots]
            : availableBots;
        }
      }

      return {
        data: finalBots as BotsResponseType,
        totalCount: headers['x-total-count'] as number,
      };
    },
    queryKey: [
      'bots',
      {
        limit,
        category,
        order,
        creator,
        shouldPin,
        shouldIncludeKnowzAI,
        isWorkspace,
        workspace: workspace?.id,
      },
    ],
    options: {
      enabled: isUserAuthenticated && isStorageReady,
      retry: false,
      select: (data) => {
        if (!filteredValue) {
          return data;
        }
        const filteredBots = data.data.filter((botItem) => {
          const botName = botItem.name.toLowerCase();
          const searchValue = filteredValue.toLowerCase();

          return botName.includes(searchValue);
        });

        return {
          ...data,
          data: filteredBots,
        };
      },
    },
  });
}

function calculateCreator(
  isWorkspace: boolean,
  creator?: Exclude<AllBotsCreatorType, 'Everyone'>,
) {
  if (!isWorkspace && creator === 'Knowz') {
    return {
      myBots: false,
      defaultBots: isWorkspace ? false : true,
      communityBots: false, //TODO: add this back once we have bot creation for community
    };
  }

  if (creator === 'You') {
    return {
      myBots: true,
      defaultBots: false,
      communityBots: false, //TODO: add this back once we have bot creation for community
    };
  }

  if (creator === 'Community') {
    return {
      myBots: false,
      defaultBots: false,
      communityBots: false, //TODO: add this back once we have bot creation for community
    };
  }

  return {
    myBots: true,
    defaultBots: isWorkspace ? false : true,
    communityBots: false, //TODO: add this back once we have bot creation for community
  };
}
